/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuthenticationContext } from '@opengeoweb/authentication';
import { useConfirmationDialog, usePrevious } from '@opengeoweb/shared';
import type { DraggableSize, Position } from '@opengeoweb/shared';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import type { MapPreset, uiTypes } from '@opengeoweb/store';
import { uiActions, useSetupDialog } from '@opengeoweb/store';
import { WarningEventFormDialog } from './WarningEventFormDialog';
import {
  useMutateDeleteWarningEvent,
  useMutateEventEditor,
  usePublishWarningEvent,
  useSaveWarningEvent,
  useWarningEventsList,
} from '../../../api/warning-api/hooks';
import { warningsEventFormActions } from '../../../store/warningsEventForm';
import {
  getSelectedEventId,
  getWarningEvent,
  getWarningsEventIsFormDirty,
  selectWarningsEventFormState,
} from '../../../store/warningsEventForm/selectors';
import { getWarningsFilterById } from '../../../store/warningsFilters/selectors';
import { PublicWarningStatus } from '../../../store/warningsForm/types';
import type { WarningEvent } from '../../../store/warningsForm/types';
import {
  warningEventDialogType,
  warningEventsSummaryDialogType,
} from '../../../store/warningsForm/utils';
import { useWarningsTranslation } from '../../../utils/i18n';
import { constructWarningWithNewStatus } from '../WarningEventForm/utils';

export interface WarningEventDialogConnectProps {
  defaultMapPreset?: MapPreset;
  bounds?: string;
  source?: uiTypes.Source;
  size?: DraggableSize;
  startPosition?: Position;
  startPositionSummary?: Position;
  isDefaultCollapsed?: boolean;
}

export const WarningEventDialogConnect: React.FC<
  WarningEventDialogConnectProps
> = ({
  defaultMapPreset,
  bounds,
  source = 'app',
  size,
  startPosition,
  startPositionSummary,
  isDefaultCollapsed = false,
}) => {
  const { t } = useWarningsTranslation();
  const dispatch = useDispatch();
  const confirmDialog = useConfirmationDialog();
  const { auth } = useAuthenticationContext();

  const selectedEventId = useSelector(getSelectedEventId);
  const previousSelectedEventId = usePrevious(selectedEventId);

  const isFormDirty = useSelector(getWarningsEventIsFormDirty);
  const savedFilterState = useSelector(getWarningsFilterById);

  const eventState = useSelector(getWarningEvent);
  const { status, editor } = eventState || {};

  const {
    data: warningEvents,
    isFetching,
    refetch: refetchEvents,
  } = useWarningEventsList(false, savedFilterState.time);
  const queryClient = useQueryClient();

  const selectedEvent = warningEvents?.find(
    (event) => event.eventId === selectedEventId,
  );

  const previousSelectedEvent = usePrevious(selectedEvent);

  const [hasFetched, setHasFetched] = React.useState(isFetching);

  const isEditor = selectedEvent?.editor === auth?.username || false;

  React.useEffect(() => {
    if (isFetching) {
      setHasFetched(true);
    }
  }, [isFetching]);

  const { dialogOrder, setDialogOrder, isDialogOpen, uiSource, onCloseDialog } =
    useSetupDialog(warningEventDialogType, source);

  const {
    dialogOrder: dialogOrderSummary,
    setDialogOrder: setDialogOrderSummary,
    isDialogOpen: isDialogOpenSummary,
    uiSource: uiSourceSummary,
    onCloseDialog: onCloseDialogSummary,
  } = useSetupDialog(warningEventsSummaryDialogType, source);

  const onOpenSummary = (): void => {
    dispatch(
      uiActions.setToggleOpenDialog({
        type: warningEventsSummaryDialogType,
        setOpen: true,
      }),
    );
  };

  const onAddSummary = (): void => {
    dispatch(
      snackbarActions.openSnackbar({
        type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
        message: t('warning-event-summary-dialog-OK-button-snackbar'),
      }),
    );
    onCloseDialogSummary();
  };

  const onFormDirty = (isFormDirty: boolean): void => {
    dispatch(
      warningsEventFormActions.setFormDirty({
        isFormDirty,
      }),
    );
  };

  const { event, error, formState } = useSelector(selectWarningsEventFormState);
  const { mutateAsync: saveWarningEvent } = useSaveWarningEvent();
  const { mutateAsync: publishWarningEvent } = usePublishWarningEvent();

  const onSaveDraft = async (
    newFormValues: WarningEvent,
  ): Promise<Headers | null> => {
    const status = newFormValues.linked_to_id
      ? PublicWarningStatus.DRAFT_PUBLISHED
      : PublicWarningStatus.DRAFT;
    return saveWarningEvent({
      eventId: newFormValues.eventId,
      data: {
        ...newFormValues,
        status,
        editor: auth?.username,
        warnings: constructWarningWithNewStatus(newFormValues, status),
      },
    });
  };

  const onPublishForm = async (
    newFormValues: WarningEvent,
  ): Promise<Headers | null> => {
    const warningsWithStatus = constructWarningWithNewStatus(
      newFormValues,
      PublicWarningStatus.PUBLISHED,
    );
    return publishWarningEvent({
      eventId: newFormValues.eventId || newFormValues.linked_to_id || '',
      data: {
        ...(newFormValues.eventId
          ? { ...newFormValues, warnings: warningsWithStatus }
          : {
              ...newFormValues,
              eventId: newFormValues.linked_to_id,
              warnings: warningsWithStatus,
            }),
        status: PublicWarningStatus.PUBLISHED,
        editor: auth?.username,
      },
    });
  };

  const { mutateAsync: deleteWarningEvent } = useMutateDeleteWarningEvent();

  const onDeleteDraft = async (): Promise<void> => {
    // First check you are still the editor, using the freshly fetched events
    const { data: refetchedEvents } = await refetchEvents();
    const refetchedEvent = refetchedEvents?.find(
      (event) => event.eventId === selectedEventId,
    );
    if (!refetchedEvent || refetchedEvent?.editor !== auth?.username) {
      return;
    }
    void confirmDialog({
      cancelLabel: t('close-dialog-cancel'),
      confirmLabel: t('warning-dialog-delete-event-confirm'),
      title: t('warning-dialog-delete-event-title'),
      description: t('warning-dialog-delete-event-description'),
    }).then(async () => {
      await deleteWarningEvent({ eventId: refetchedEvent?.eventId! });
      onCloseDialog();
      onCloseDialogSummary();
    });
  };

  React.useEffect(() => {
    if (editor !== selectedEvent?.editor && status !== 'TODO') {
      void setIsEditor(editor !== auth?.username);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  const shouldGiveEditorWarning = React.useCallback(
    (newEditor?: string, oldEditor?: string): void => {
      const oldEditorIsYou = oldEditor && oldEditor === auth?.username;
      if (oldEditorIsYou && newEditor && newEditor !== oldEditor) {
        dispatch(
          warningsEventFormActions.setFormError({
            severity: 'warning',
            message: t('warning-event-take-over-message'),
          }),
        );
      }
    },
    [auth?.username, dispatch, t],
  );

  React.useEffect(() => {
    // Only give editor warning when the change in selectedEvent is from a refetch
    if (hasFetched) {
      setHasFetched(false);
      shouldGiveEditorWarning(
        selectedEvent?.editor,
        previousSelectedEvent?.editor,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent?.editor]);
  const updateEventEditor = useMutateEventEditor();

  const setIsEditor = async (isReadOnly: boolean): Promise<void> => {
    const username = auth?.username || '';

    // make sure to get the latest changes if you are editing an existing warning
    const updateEditor = (force: boolean): void =>
      updateEventEditor.mutate(
        {
          eventId: selectedEventId,
          isEditor: !isReadOnly,
          force,
        },
        {
          onError: (e: Error) => {
            if (e.cause === 409) {
              void confirmDialog({
                title: t('warning-edit-mode-title'),
                description: t('warning-edit-mode-description'),
                confirmLabel: t('warning-edit-mode-confirm'),
                cancelLabel: t('warning-dialog-cancel'),
              }).then(() => void updateEditor(true));
            } else {
              void refetchEvents();
            }
          },
          onSuccess: async () => {
            queryClient.setQueriesData(
              { queryKey: ['warning-event-list'], exact: false },
              (old: WarningEvent[] | undefined) => {
                if (!old) {
                  return old;
                }
                return old.map((event) =>
                  event.eventId === selectedEventId
                    ? {
                        ...event,
                        editor: isReadOnly ? undefined : auth?.username,
                      }
                    : event,
                );
              },
            );
            dispatch(
              warningsEventFormActions.setFormValues({
                formState: isReadOnly ? 'readonly' : '',
              }),
            );
          },
        },
      );

    if (
      !selectedEventId ||
      (!isReadOnly && selectedEvent?.editor === username)
    ) {
      // currently editing a new warning or
      // correct editor is already set or
      // someone else is editor so only have to set to readonly mode
      // No need for a BE call
      dispatch(
        warningsEventFormActions.setFormValues({
          formState: isReadOnly ? 'readonly' : '',
        }),
      );

      return;
    }
    void updateEditor(false);
  };

  const onChangeFormMode = (isReadOnly: boolean): void => {
    if (isReadOnly && isFormDirty) {
      void confirmDialog({
        title: t('warning-view-mode-title'),
        description: t('warning-view-mode-description'),
        confirmLabel: t('warning-view-mode-confirm'),
        cancelLabel: t('warning-dialog-cancel'),
      }).then(async () => {
        await setIsEditor(isReadOnly);
      });
    } else {
      void setIsEditor(isReadOnly);
    }
  };

  // Readonly in case you are not creating a new warning and you are not set as the editor
  const isReadOnly =
    formState === 'readonly' || (!isEditor && status !== undefined);

  // Remove any errors when switching events
  React.useEffect(() => {
    if (selectedEventId !== previousSelectedEventId) {
      dispatch(warningsEventFormActions.removeFormError());
    }
  }, [selectedEventId, previousSelectedEventId, dispatch]);

  // Only show toggle if in draft status
  const shouldHideFormToggleMode =
    status !== 'DRAFT' && status !== 'DRAFT_PUBLISHED';

  return (
    <WarningEventFormDialog
      bounds={bounds}
      currentEventEditor={selectedEvent?.editor}
      error={error}
      isOpen={isDialogOpen}
      isReadOnly={isReadOnly}
      isSummaryOpen={isDialogOpenSummary}
      onAddSummary={onAddSummary}
      onChangeFormMode={onChangeFormMode}
      onClose={(): void => {
        onCloseDialog();
        onCloseDialogSummary();
      }}
      onCloseSummary={onCloseDialogSummary}
      onDelete={onDeleteDraft}
      onFormDirty={onFormDirty}
      onMouseDown={setDialogOrder}
      onMouseDownSummary={setDialogOrderSummary}
      onOpenSummary={onOpenSummary}
      onPublish={onPublishForm}
      onSaveDraft={onSaveDraft}
      order={dialogOrder}
      selectedEvent={event}
      shouldHideFormToggleMode={shouldHideFormToggleMode}
      size={size}
      source={uiSource}
      startPosition={startPosition}
      startPositionSummary={startPositionSummary}
      summaryOrder={dialogOrderSummary}
      summarySource={uiSourceSummary}
    />
  );
};
